import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/common/Command';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/common/Popover';
import { cn } from '@/lib/cn';
import { useEffect, useRef, useState } from 'react';
import type { InputProps } from './Input';

export interface ComboboxItem {
  id: string | number;
  value: string;
  label: string;
}

export interface ComboboxProps extends Omit<InputProps, 'value' | 'onChange'> {
  items: Array<ComboboxItem>;
  value?: string | number;
  onChange?: (value: string | undefined) => void;
  commandClassName?: string;
  popoverContentClassName?: string;
  emptyText?: string;
}

export const Combobox = ({
  items,
  value: selectedItemId,
  onChange,
  commandClassName,
  popoverContentClassName,
  emptyText = 'No results found.',
  onBlur,
  onFocus,
  className,
  ...props
}: ComboboxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const previousSelectedItemIdRef = useRef(selectedItemId);
  const contentRef = useRef<HTMLDivElement>(null);

  const selectedItem = selectedItemId
    ? items.find((item) => item.id.toString() === selectedItemId.toString())
    : null;

  useEffect(() => {
    if (previousSelectedItemIdRef.current !== selectedItemId) {
      previousSelectedItemIdRef.current = selectedItemId;

      if (selectedItem && !isOpen) {
        setSearchValue(selectedItem.label);
      } else if (!selectedItemId && !isOpen) {
        setSearchValue('');
      }
    }
  }, [selectedItemId, selectedItem, isOpen]);

  const filteredItems = searchValue
    ? items.filter((item) =>
        item.label.toLowerCase().includes(searchValue.toLowerCase()),
      )
    : items;

  const handleSelect = (item: ComboboxItem) => {
    setSearchValue(item.label);
    onChange?.(item.id.toString());
    setIsOpen(false);
  };

  const handleValueChange = (newValue: string) => {
    setSearchValue(newValue);

    if (selectedItem && newValue !== selectedItem.label) {
      onChange?.(undefined);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsOpen(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Keep open when focus/pointer moves into the portaled list (e.g. scrollbar).
    const next = e.relatedTarget as Node | null;
    if (next && contentRef.current?.contains(next)) {
      return;
    }
    // relatedTarget is often null on scrollbar mousedown — defer and re-check.
    requestAnimationFrame(() => {
      if (contentRef.current?.contains(document.activeElement)) {
        return;
      }
      setIsOpen(false);
      onBlur?.(e);
    });
  };

  return (
    <Command shouldFilter={false} className={cn('bg-white', commandClassName)}>
      {/*
        modal=true: include the portaled list in Radix scroll-lock "shards"
        so wheel/touch scroll works inside dialogs (body scroll is locked).
      */}
      <Popover open={isOpen} modal>
        <PopoverAnchor className={className}>
          <CommandInput
            value={searchValue}
            onValueChange={handleValueChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
        </PopoverAnchor>

        <PopoverContent
          ref={contentRef}
          className={cn(
            'bg-background border-muted data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 overflow-hidden rounded-md border p-0 shadow-md',
            popoverContentClassName,
          )}
          align="start"
          side="bottom"
          autoFocus={false}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Don't dismiss when interacting with the list / scrollbar.
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest('[data-slot="command-input"]')) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => e.stopPropagation()}
          sideOffset={4}
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <CommandList className="max-h-60 overscroll-contain p-1">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.value}
                  onSelect={() => handleSelect(item)}
                  // Prevent input blur (which would close the list) before select.
                  onMouseDown={(e) => e.preventDefault()}
                  className="text-foreground data-[selected=true]:bg-secondary data-[selected=true]:text-primary-foreground py-2 pr-2 pl-8 opacity-100"
                >
                  {item.label}
                </CommandItem>
              ))
            ) : (
              <div className="text-muted-foreground py-6 text-center text-sm">
                {emptyText}
              </div>
            )}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
};
